import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Sheet from "../Sheet.jsx";
import HoverTooltip from "../HoverTooltip.jsx";
import { useQuery } from "../../lib/dataClient.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact, sumSeries } from "../../lib/renderers.js";
import { DIMENSIONS, scopeStepLabel } from "../../lib/dimensionDefs.js";
import { observeSettledSize } from "../../lib/settledResize.js";
import { layoutPagedRow, pageWindowStart, jumpWindowStart } from "../../lib/positionRibbon.js";
import { usePositionFlows } from "../../lib/usePositionFlows.js";
import PositionFlowOverlay from "./PositionFlowOverlay.jsx";

// ScopePositionStack — "where you are" across the whole drill path, stacked.
//
// One ROW per level of the scope path. Each row is that level's dimension ranked
// by activity and shown as a PAGED strip of cells (positionRibbon.js): the row
// always fills the width — one member fills it whole, a handful divide it evenly,
// and thousands page through a window sized to a minimum cell width. You open on
// the page holding your own cell; a cursor under the row shows where that window
// sits in the full ranking, and the edge chips page toward the bigger / smaller
// tail. Cell WIDTH is uniform (a page slot); activity rides cell HEAT.
//
// Reading top→bottom walks the drill: Clusters → Endpoints → Models, each a page
// with your cell lit. Clicking a cell jumps across at that level (sibling swap).
// Clusters keep the hierarchy-aware icicle (ClusterAnatomyBody); this is for the
// flat/mixed dims that have no tree.

const MAX_LEVELS = 6;   // fixed useQuery slots — scope depth is bounded by the dim count
const ROW_H = 30;       // px cell height
const ROW_GAP = 28;     // px between stacked rows — the gutter the flow ribbons live in
const MIN_CELL_W = 72;  // px floor per cell → sets page size

export default function ScopePositionStack({ scopePath = [], range, onNavigatePath, descent = [], descentLoading = false }) {
  const path = scopePath || [];
  const enabled = path.length > 0;
  const atRoot = !enabled; // the global scope — committed rows are empty; show a Global origin

  // Fixed-shape per-level fetches: level i = the distribution of step i's dim
  // within the prefix before it. limit is generous so the ranking + rank readout
  // are accurate; the row only ever paints one page of it.
  const slots = [];
  for (let i = 0; i < MAX_LEVELS; i++) {
    const on = enabled && i < path.length;
    const step = on ? path[i] : null;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data } = useQuery(
      "getDimensionEntries",
      { dimKey: step?.dim || "endpoints", parentScopePath: on ? path.slice(0, i) : [], limit: 400, range, sparkline: false },
      { enabled: on },
    );
    slots.push(on ? data : null);
  }

  // The terminal member's own total — so the deepest row can place you even if
  // you rank past the fetched page.
  const { data: bundle } = useQuery(
    "getOverviewActivityBundle",
    { scopePath: path, bucketCount: 1, topN: 1, range },
    { enabled },
  );
  const terminalTotal = useMemo(() => sumSeries(bundle?.prompts?.total), [bundle]);

  const hostRef = useRef(null);
  const [width, setWidth] = useState(0);
  // rowGeom: strip y-bands + on-screen cell positions per level, for the flow
  // overlay. Bumped by layoutTick whenever a row pages (its cells move).
  const [geom, setGeom] = useState(null);
  const [layoutTick, setLayoutTick] = useState(0);
  const onCellsChange = useCallback(() => setLayoutTick((t) => t + 1), []);

  const measure = useCallback(() => {
    const grid = hostRef.current;
    if (!grid) return;
    const gr = grid.getBoundingClientRect();
    setWidth(Math.round(gr.width));
    const strips = [...grid.querySelectorAll("[data-pos-strip]")];
    const byIdx = [];
    for (const el of strips) {
      const idx = Number(el.getAttribute("data-level-index"));
      const r = el.getBoundingClientRect();
      const cells = [...el.querySelectorAll("[data-pos-cell]")].map((c) => {
        const cr = c.getBoundingClientRect();
        return {
          id: c.getAttribute("data-cell-id"),
          cx: cr.left - gr.left + cr.width / 2,
          w: cr.width,
          selected: c.getAttribute("data-selected") === "1",
          value: Number(c.getAttribute("data-cell-value")) || 0,
        };
      });
      byIdx[idx] = { top: r.top - gr.top, bottom: r.bottom - gr.top, cells };
    }
    setGeom({ width: gr.width, height: gr.height, rows: byIdx });
  }, []);
  useEffect(() => {
    const grid = hostRef.current;
    if (!grid) return undefined;
    measure();
    return observeSettledSize(grid, measure);
  }, [measure]);
  const barW = Math.max(80, width);

  const rows = useMemo(() => {
    // Only the first MAX_LEVELS steps have a fetched slot; mapping the whole
    // path when it's deeper would render synthetic "#1 of 1" rows off the end.
    return path.slice(0, MAX_LEVELS).map((step, i) => {
      const sid = String(step.ids?.[0] ?? step.id ?? "");
      const isTerminal = i === path.length - 1;
      let members = (slots[i]?.entries || []).map((e) => ({
        id: String(e.id), label: e.label, value: Math.max(0, Number(e.activity) || 0),
      }));
      let idx = members.findIndex((m) => m.id === sid);
      if (idx < 0 && sid) {
        members = [...members, { id: sid, label: scopeStepLabel(step), value: isTerminal ? terminalTotal : 0 }];
        members.sort((a, b) => b.value - a.value);
        idx = members.findIndex((m) => m.id === sid);
      }
      return {
        dim: step.dim,
        dimLabel: DIMENSIONS[step.dim]?.label || step.dim,
        prefix: path.slice(0, i),
        selectedId: sid,
        members,
        rank: idx >= 0 ? idx + 1 : null,
        distinct: slots[i]?.total ?? members.length,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...slots, terminalTotal, JSON.stringify(path)]);

  // Levels in display order — committed rows then the projected descent — feed
  // the vertical sankey. Each carries its dim, prefix, top members and your
  // selection; the flow hook turns adjacent pairs into spine + cross-link
  // ribbons (see usePositionFlows). levelIndex on each rendered row aligns the
  // measured geometry with a flow's `pair`.
  const sequence = useMemo(() => {
    const committed = rows.map((r) => ({ dim: r.dim, prefix: r.prefix, members: r.members, selectedId: r.selectedId }));
    const proj = (descent || []).map((d) => ({ dim: d.dim, prefix: d.prefix, members: d.members, selectedId: d.selectedId }));
    return [...committed, ...proj];
  }, [rows, descent]);
  const flows = usePositionFlows(sequence, range);

  const [hovered, setHovered] = useState(null); // { row, id } — focused flow cell
  const scopeKey = useMemo(() => JSON.stringify(path), [path]);
  useEffect(() => { setHovered(null); }, [scopeKey]);

  // Re-measure when the row set or paging changes (new dims, descent resolves,
  // a row pages) — size-only observers miss these since the box is unchanged.
  const layoutSig = `${rows.length}|${descent.length}|${barW}|${flows.length}`;
  useEffect(() => { measure(); }, [layoutSig, layoutTick, measure]);

  return (
    <Sheet eyebrow="Position" title="Where you are" diagram tight>
      <span className={`${NOTE_CLASS} block pb-[6px]`}>
        {atRoot
          ? "from the global scope, auto-drilled down the busiest branch · click any cell to jump there"
          : "your place at each level of the drill · dashed rows auto-continue down the busiest branch · click any cell to jump there"}
      </span>
      <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] p-[8px]">
        <div className="relative">
          {/* Flow ribbons UNDER the rows (z-0) — the labels/pager (z-1) occlude
              any ribbon passing behind them. */}
          {geom && flows.length ? (
            <PositionFlowOverlay width={geom.width} height={geom.height} rows={geom.rows} flows={flows} hovered={hovered} />
          ) : null}
          <div ref={hostRef} className="relative z-[1] grid" style={{ rowGap: ROW_GAP }} onMouseLeave={() => setHovered(null)}>
          {/* Global origin — the whole "trace lake", the root the descent tours
              from. Clicking zooms out to it. */}
          {atRoot ? (
            <div className="grid gap-[4px]">
              <div className="flex items-baseline justify-between gap-[8px]">
                <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Global</span>
                <span className={`${NOTE_CLASS} text-[10px]`}>all activity</span>
              </div>
              <button
                type="button"
                onClick={() => onNavigatePath?.([])}
                className="grid h-[30px] items-center overflow-hidden rounded-[2px] border border-[color:var(--line-soft)] px-[6px] text-left cursor-pointer"
                style={{ backgroundColor: "color-mix(in srgb, var(--green) 22%, transparent)" }}
              >
                <span className="truncate text-[10.5px] leading-[1.1] text-[color:var(--ink)]">All activity</span>
              </button>
            </div>
          ) : null}

          {rows.map((row, i) => (
            // Key by selection so a new scope opens a freshly-centered window.
            <RibbonRow key={`${i}:${row.selectedId}`} row={row} barW={barW} levelIndex={i}
              onCellHover={(id) => setHovered({ row: i, id })}
              onCellsChange={onCellsChange}
              onPick={(id) => onNavigatePath?.([...row.prefix, { dim: row.dim, ids: [String(id)], id: String(id) }])} />
          ))}

          {/* The auto-drilled descent — the stack continues itself down the
              busiest branch (greedy by prompt volume, top member per level;
              see useDiscriminatingDescent). Dashed/dimmed to read as projected,
              not committed. Clicking a cell commits it. */}
          {descent.length || descentLoading ? (
            <div className="mt-[2px] grid" style={{ rowGap: ROW_GAP }}>
              <div className="flex items-center gap-[8px] pt-[2px]">
                <span className="h-px flex-1 bg-[color:var(--line-soft)]" aria-hidden="true" />
                <span className="text-[9px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">highest-volume descent</span>
                <span className="h-px flex-1 bg-[color:var(--line-soft)]" aria-hidden="true" />
              </div>
              {descent.length
                ? descent.map((row, i) => (
                    <RibbonRow
                      key={`d${i}:${row.selectedId}`}
                      row={row}
                      barW={barW}
                      projected
                      levelIndex={path.length + i}
                      onCellHover={(id) => setHovered({ row: path.length + i, id })}
                      onCellsChange={onCellsChange}
                      onPick={(id) => onNavigatePath?.([...row.prefix, { dim: row.dim, ids: [String(id)], id: String(id) }])}
                    />
                  ))
                : Array.from({ length: 3 }, (_, i) => <DescentSkeletonRow key={`sk${i}`} dim={i} />)}
            </div>
          ) : null}
          </div>
        </div>
      </div>
    </Sheet>
  );
}

function RibbonRow({ row, barW, onPick, projected = false, levelIndex = 0, onCellHover, onCellsChange }) {
  // null → layout centers the window on you; a number pins a paged position.
  const [windowStart, setWindowStart] = useState(null);
  const { cells, windowStart: start, visible, total, selectedIndex } = useMemo(
    () => layoutPagedRow(row.members, row.selectedId, { width: barW, minCellW: MIN_CELL_W, windowStart }),
    [row.members, row.selectedId, barW, windowStart],
  );
  const maxVal = row.members.reduce((m, x) => Math.max(m, x.value), 0) || 1;
  const paged = total > visible;
  const before = start;
  const after = Math.max(0, total - start - visible);
  const page = (dir) => setWindowStart(pageWindowStart(start, dir, visible, total));
  const jumpTo = (frac) => setWindowStart(jumpWindowStart(frac, visible, total));

  // Tell the parent the on-screen cells moved (paging) so the flow overlay
  // re-measures — a size observer can't see it, the strip box is unchanged.
  useEffect(() => { onCellsChange?.(); }, [start, row.members, barW, onCellsChange]);

  return (
    <div className="grid gap-[4px]">
      {/* Label ABOVE the row — dim on the left, your rank on the right — so the
          cells span the full width beneath it. */}
      <div className="flex items-baseline justify-between gap-[8px]">
        <span className="min-w-0 truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
          {projected ? <span className="mr-[5px] rounded-[2px] border border-dashed border-[color:var(--line-soft)] px-[3px] py-px text-[8px] tracking-[0.04em] text-[color:var(--muted)]">auto</span> : null}
          {row.dimLabel}
        </span>
        <span className={`${NOTE_CLASS} shrink-0 text-[10px]`}>
          {projected
            // Projected rows only fetched a top-K scoring slice, so an in-slice
            // "#rank of N" would misstate the true breadth — show the real
            // distinct count instead.
            ? (row.distinct ? `${formatCompact(row.distinct)} distinct` : `of ${formatCompact(total)}`)
            : (row.rank != null ? `#${formatCompact(row.rank)} of ${formatCompact(total)}` : `of ${formatCompact(total)}`)}
        </span>
      </div>
      {/* Cells fill the full row — no overlaid controls, so nothing is clipped
          mid-cell. Uniform width (a page slot); activity rides cell heat. */}
        <div className="relative overflow-hidden" style={{ height: ROW_H }} data-pos-strip data-level-index={levelIndex}>
          {cells.map((c) => {
            const isSelf = c.selected;
            const pct = isSelf ? 88 : Math.round((0.14 + 0.5 * Math.sqrt(Math.max(0, Math.min(1, c.value / maxVal)))) * 100);
            const tip = `${c.label} · ${formatCompact(c.value)} · #${formatCompact(c.index + 1)}`;
            return (
              <HoverTooltip key={c.id} label={tip} placement="top">
                <button
                  type="button"
                  // In a projected (auto) row the highlighted cell IS the drill
                  // target, so it stays clickable; in a committed row it's you.
                  onClick={() => (projected || !isSelf) && onPick(c.id)}
                  onMouseEnter={onCellHover ? () => onCellHover(String(c.id)) : undefined}
                  data-pos-cell
                  data-cell-id={String(c.id)}
                  data-cell-value={c.value}
                  data-selected={isSelf ? "1" : "0"}
                  aria-label={tip}
                  className={[
                    "absolute top-0 grid h-full items-center overflow-hidden rounded-[2px] border px-[6px] text-left",
                    isSelf
                      ? `border-[color:var(--green)] z-[5] ${projected ? "cursor-pointer" : "cursor-default"}`
                      : `${projected ? "border-dashed" : ""} border-[color:var(--line-soft)] cursor-pointer hover:border-[color:var(--line-strong)]`,
                  ].join(" ")}
                  style={{ left: c.x, width: Math.max(1, c.w - 1), backgroundColor: `color-mix(in srgb, var(--green) ${pct}%, transparent)` }}
                >
                  {c.w >= 40 || isSelf ? (
                    <span className="truncate text-[10.5px] leading-[1.1]" style={{ color: isSelf ? "var(--bg)" : "var(--ink)" }}>
                      {c.label}
                    </span>
                  ) : null}
                </button>
              </HoverTooltip>
            );
          })}
        </div>
        {/* Pagination cursor — BELOW the cells (so they fill the container): page
            arrows carrying the off-frame counts, flanking a track that shows the
            window span + your mark in the full ranking. Click the track to page. */}
        {paged ? (
          <div className="flex items-center gap-[8px]">
            <button
              type="button" disabled={before === 0} onClick={() => page(-1)}
              aria-label={`${before} higher-ranked. Previous page.`}
              className="shrink-0 text-[9px] leading-none tabular-nums text-[color:var(--muted)] disabled:opacity-30 enabled:cursor-pointer enabled:hover:text-[color:var(--ink)]"
            >
              ◂ {before > 0 ? formatCompact(before) : "0"}
            </button>
            <button
              type="button"
              onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); jumpTo((e.clientX - r.left) / Math.max(1, r.width)); }}
              aria-label="Ranking cursor. Select a position to jump to that page."
              className="relative h-[6px] flex-1 cursor-pointer rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)]"
            >
              <span
                className="absolute inset-y-0 rounded-[1px]"
                style={{ left: `${(start / total) * 100}%`, width: `${(visible / total) * 100}%`, backgroundColor: "color-mix(in srgb, var(--green) 40%, transparent)" }}
              />
              {selectedIndex >= 0 ? (
                <span className="absolute inset-y-[-1px] w-[2px] bg-[color:var(--green)]" style={{ left: `calc(${(selectedIndex / total) * 100}% - 1px)` }} />
              ) : null}
            </button>
            <button
              type="button" disabled={after === 0} onClick={() => page(1)}
              aria-label={`${after} lower-ranked. Next page.`}
              className="shrink-0 text-[9px] leading-none tabular-nums text-[color:var(--muted)] disabled:opacity-30 enabled:cursor-pointer enabled:hover:text-[color:var(--ink)]"
            >
              {after > 0 ? formatCompact(after) : "0"} ▸
            </button>
          </div>
        ) : null}
      </div>
  );
}

// Placeholder row while the (sequential, async) descent is still walking — a
// dimmed dashed bar per pending level with the house L→R "fetching" glint (the
// same `sparkline-shimmer` sweep EntrySparkline uses; keyframes in styles.css).
function SkeletonShimmer({ opacity = 0.6 }) {
  return (
    <span
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
      style={{
        background: "linear-gradient(90deg, transparent 0%, var(--muted-soft) 50%, transparent 100%)",
        backgroundSize: "25% 100%",
        backgroundRepeat: "no-repeat",
        animation: "sparkline-shimmer 2.6s ease-in-out infinite",
        opacity,
      }}
    />
  );
}

function DescentSkeletonRow({ dim = 0 }) {
  return (
    <div className="grid gap-[4px]" style={{ opacity: 0.75 - dim * 0.18 }} aria-hidden="true">
      <div className="flex items-baseline justify-between gap-[8px]">
        <span className="relative h-[9px] w-[72px] overflow-hidden rounded-[2px] bg-[color:var(--line-soft)]"><SkeletonShimmer opacity={0.5} /></span>
        <span className="relative h-[9px] w-[40px] overflow-hidden rounded-[2px] bg-[color:var(--line-soft)]"><SkeletonShimmer opacity={0.5} /></span>
      </div>
      <div
        className="relative overflow-hidden rounded-[2px] border border-dashed border-[color:var(--line-soft)]"
        style={{ height: ROW_H, backgroundColor: "color-mix(in srgb, var(--line-soft) 60%, transparent)" }}
      >
        <SkeletonShimmer />
      </div>
    </div>
  );
}
